/**
 * Sentry Configuration for ORCA360 Frontend
 * Sprint 5, Day 1: Error tracking and monitoring
 *
 * Initializes Sentry for error tracking, performance monitoring, and session replays
 */

import * as Sentry from '@sentry/react';

export function initSentry() {
  // Only initialize Sentry in production or staging
  if (import.meta.env.MODE === 'development') {
    console.log('[Sentry] Skipping initialization in development mode');
    return;
  }

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!sentryDsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,

    // Performance Monitoring sample rate (10% of transactions)
    tracesSampleRate: 0.1,

    // Session Replay sample rate
    // Sample 10% of all sessions
    replaysSessionSampleRate: 0.1,
    // Sample 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // Random plugins/extensions
      'fb_xd_fragment',
    ],

    // Filter sensitive data before sending
    beforeSend(event) {
      // Remove sensitive data from event
      if (event.request) {
        delete event.request.cookies;

        // Remove sensitive headers
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
      }

      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            delete breadcrumb.data.authorization;
            delete breadcrumb.data.password;
          }
          return breadcrumb;
        });
      }

      return event;
    },
  });

  console.log(`[Sentry] Initialized in ${import.meta.env.MODE} environment`);
}

/**
 * Capture an error manually with context
 */
export function captureError(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  });
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}
